import React from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, IconButton, Paper, FormControl, InputLabel, Select, MenuItem, Autocomplete, Box
} from "@mui/material";
import { Remove, Add } from "@mui/icons-material";

// Tipos
type BaseCompra = {
  proveedor: string;
  monedaCosto: string;
  precioCosto: number;
  nroFile?: string;
};

type ServicioAereo = {
  id: string;
  aerolinea: string;
  destino: string;
  descripcion: string;
  moneda: string;
  precioVenta: number;
  compra: BaseCompra;
};

type ServicioAlojamiento = {
  id: string;
  hotel: string;
  destino: string;
  noches: number;
  serviciosIncluidos: string;
  moneda: string;
  precioVenta: number;
  compra: BaseCompra;
};

type ServicioExtra = {
  id: string;
  servicio: string;
  moneda: string;
  precioVenta: number;
  compra: BaseCompra;
};

type ServiceType = 'aereo' | 'alojamiento' | 'extra';

interface ServiceTableProps {
  type: ServiceType;
  services: ServicioAereo[] | ServicioAlojamiento[] | ServicioExtra[];
  onAdd: () => void;
  onUpdate: (id: string, campo: any, valor: any) => void;
  onDelete: (id: string) => void;
  opcionesProveedores: string[];
  opcionesEspecificas: string[];
}

export const ServiceTable: React.FC<ServiceTableProps> = ({
  type,
  services,
  onAdd,
  onUpdate,
  onDelete,
  opcionesProveedores,
  opcionesEspecificas
}) => {
  // Headers según tipo de servicio
  const getHeaders = () => {
    switch (type) {
      case 'aereo':
        return ['Aerolínea', 'Destino', 'Descripción del vuelo', 'Moneda', 'Precio de venta', 'Acciones'];
      case 'alojamiento':
        return ['Hotel', 'Destino', '# noches', 'Servicios incluidos', 'Moneda', 'Precio de venta', 'Acciones'];
      case 'extra':
        return ['Servicios extra', 'Moneda', 'Precio de venta', 'Acciones'];
    }
  };

  // Renderizar fila principal según tipo
  const renderMainRow = (servicio: any, idx: number) => {
    switch (type) {
      case 'aereo':
        return (
          <>
            <TableCell>
              <Autocomplete
                freeSolo
                options={opcionesEspecificas}
                value={servicio.aerolinea}
                onChange={(_, newValue) => onUpdate(servicio.id, 'aerolinea', newValue || '')}
                onInputChange={(_, newInput) => onUpdate(servicio.id, 'aerolinea', newInput)}
                renderInput={(params) => (
                  <TextField {...params} size="small" placeholder="Ej: Aerolíneas Argentinas" fullWidth />
                )}
                sx={{ minWidth: 200 }}
              />
            </TableCell>
            <TableCell>
              <TextField
                size="small"
                value={servicio.destino}
                onChange={(e) => onUpdate(servicio.id, 'destino', e.target.value)}
                placeholder="Ej: Río de Janeiro"
              />
            </TableCell>
            <TableCell>
              <TextField
                size="small"
                value={servicio.descripcion}
                onChange={(e) => onUpdate(servicio.id, 'descripcion', e.target.value)}
                placeholder="Ej: SCL-GIG-SCL"
              />
            </TableCell>
          </>
        );
      
      case 'alojamiento':
        return (
          <>
            <TableCell>
              <Autocomplete
                freeSolo
                options={opcionesEspecificas}
                value={servicio.hotel}
                onChange={(_, newValue) => onUpdate(servicio.id, 'hotel', newValue || '')}
                onInputChange={(_, newInput) => onUpdate(servicio.id, 'hotel', newInput)}
                renderInput={(params) => (
                  <TextField {...params} size="small" placeholder="Ej: Sheraton" fullWidth />
                )}
                sx={{ minWidth: 200 }}
              />
            </TableCell>
            <TableCell>
              <TextField
                size="small"
                value={servicio.destino}
                onChange={(e) => onUpdate(servicio.id, 'destino', e.target.value)}
                placeholder="Ej: Buenos Aires"
              />
            </TableCell>
            <TableCell>
              <TextField
                size="small"
                type="number"
                value={servicio.noches || ''}
                onChange={(e) => onUpdate(servicio.id, 'noches', parseInt(e.target.value) || 0)}
                inputProps={{ min: 0 }}
                placeholder="0"
              />
            </TableCell>
            <TableCell>
              <TextField
                size="small"
                value={servicio.serviciosIncluidos}
                onChange={(e) => onUpdate(servicio.id, 'serviciosIncluidos', e.target.value)}
                placeholder="Ej: Desayuno incluido"
              />
            </TableCell>
          </>
        );
      
      case 'extra':
        return (
          <TableCell colSpan={2}>
            <TextField
              size="small"
              value={servicio.servicio}
              onChange={(e) => onUpdate(servicio.id, 'servicio', e.target.value)}
              placeholder="Ej: Excursión a Cristo Redentor"
            />
          </TableCell>
        );
    }
  };

  // Renderizar fila de compra (común para todos)
  const renderCompraRow = (servicio: any) => {
    const colSpan = type === 'alojamiento' ? 2 : type === 'aereo' ? 2 : 1;
    
    return (
      <TableRow>
        <TableCell colSpan={colSpan}>
          <Autocomplete
            freeSolo
            options={opcionesProveedores}
            value={servicio.compra?.proveedor || ''}
            onChange={(_, v) => onUpdate(servicio.id, 'compra', { ...servicio.compra, proveedor: v || '' })}
            onInputChange={(_, v) => onUpdate(servicio.id, 'compra', { ...servicio.compra, proveedor: v || '' })}
            renderInput={(params) => <TextField {...params} size="small" label="Proveedor" placeholder="Ej: Proveedor" />}
          />
        </TableCell>
        <TableCell sx={{ pl: 8 }}>
          <FormControl size="small" sx={{ minWidth: 80 }}>
            <InputLabel>Moneda costo</InputLabel>
            <Select
              label="Moneda costo"
              value={servicio.compra?.monedaCosto || 'USD'}
              onChange={(e) => onUpdate(servicio.id, 'compra', { ...servicio.compra, monedaCosto: e.target.value })}
            >
              <MenuItem value="USD">USD</MenuItem>
              <MenuItem value="ARS">ARS</MenuItem>
            </Select>
          </FormControl>
        </TableCell>
        <TableCell sx={{ pl: 2 }}>
          <TextField
            size="small"
            type="number"
            label="Precio costo"
            value={servicio.compra?.precioCosto || ''}
            onChange={(e) => onUpdate(servicio.id, 'compra', { ...servicio.compra, precioCosto: parseFloat(e.target.value) || 0 })}
            inputProps={{ min: 0, step: 0.01 }}
            placeholder="0"
          />
        </TableCell>
        <TableCell>
          <TextField
            size="small"
            label="Nro. de FILE"
            value={servicio.compra?.nroFile || ''}
            onChange={(e) => onUpdate(servicio.id, 'compra', { ...servicio.compra, nroFile: e.target.value })}
            placeholder="Opcional"
          />
        </TableCell>
        {type === 'alojamiento' && (
          <>
            <TableCell />
            <TableCell />
          </>
        )}
      </TableRow>
    );
  };

  return (
    <>
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              {type === 'extra' ? (
                <>
                  <TableCell key="servicio" colSpan={2} align="left">Servicios extra</TableCell>
                  <TableCell key="moneda" align="left">Moneda</TableCell>
                  <TableCell key="precio" align="left">Precio de venta</TableCell>
                  <TableCell key="acciones" align="center">Acciones</TableCell>
                </>
              ) : (
                getHeaders().map((header, idx) => (
                  <TableCell key={idx} align={header === 'Acciones' ? 'center' : 'left'}>
                    {header}
                  </TableCell>
                ))
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {services.map((servicio: any, idx) => (
              <React.Fragment key={type === 'alojamiento' ? `${type}-${servicio.id}-${idx}` : servicio.id}>
                <TableRow>
                  {renderMainRow(servicio, idx)}
                  <TableCell>
                    <FormControl size="small" sx={{ minWidth: 80 }}>
                      <Select
                        value={servicio.moneda}
                        onChange={(e) => onUpdate(servicio.id, 'moneda', e.target.value)}
                      >
                        <MenuItem value="USD">USD</MenuItem>
                        <MenuItem value="ARS">ARS</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      value={servicio.precioVenta || ''}
                      onChange={(e) => onUpdate(servicio.id, 'precioVenta', parseFloat(e.target.value) || 0)}
                      inputProps={{ min: 0, step: 0.01 }}
                      placeholder="0"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => onDelete(servicio.id)} color="error">
                      <Remove />
                    </IconButton>
                  </TableCell>
                </TableRow>
                {renderCompraRow(servicio)}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-start' }}>
        <IconButton
          onClick={onAdd}
          color="primary"
          sx={{ backgroundColor: '#461264', color: 'white', '&:hover': { backgroundColor: '#5a1a7a' } }}
        >
          <Add />
        </IconButton>
      </Box>
    </>
  );
};
